import { apiFetch } from "./client";
import type {
  Regola,
  RegolaInput,
  SimulaResult,
  CommissioniCross,
  ReconcilePreview,
  ReconcileStatus,
  LookupItem,
  OperatoriOut,
  PipelineOut,
  SerieOut,
  MargineOut,
  A2AInviabili,
  A2AInviabiliParams,
  A2AKpi,
  A2APrefill,
  A2AInviaResult,
  A2AStatus,
  Storno,
  AutoInvioStatus,
  SentraInviabili,
  SentraKpi,
  SentraPrefill,
  SentraInviaResult,
  SentraStatus,
  SentraLoginStartOut,
  SentraLoginStatusOut,
  IrenLoginStartOut,
  IrenLoginStatusOut,
  IrenInviabili,
  IrenKpi,
  IrenPrefill,
  IrenInviaResult,
  IrenStatus,
  AffRichiesta,
  AffInvito,
  AffKpi,
  AffAnomalie,
  AffEsitoInvio,
  AgenziaNonAssegnata,
  CatalogoUtenze,
  NuovaUtenzaBody,
  UtenzaCatalogo,
  UtenzaEvento,
  UtenzaPredefinitaBody,
  ReteResponsabile,
  ReteResponsabileBody,
  ReteResponsabileUpdate,
  ReteScheda,
  ReteCompletaBody,
  ReteIncaricoBody,
  ReteEvento,
  ReteQuotaResponsabile,
} from "@/types/admin";
import type { ProvaAccessoStato } from "@/types";
import type { QuotaReteResponse } from "@/types/rete";

function rangeParams(from: string, to: string, agenziaIds: string[]): string {
  const q = new URLSearchParams({ from, to });
  agenziaIds.forEach((id) => q.append("agenzia_ids", id));
  return q.toString();
}

export const adminApi = {
  getAutoInvio: () => apiFetch<AutoInvioStatus>("/admin/auto-invio"),
  setAutoInvio: (enabled: boolean) =>
    apiFetch<AutoInvioStatus>("/admin/auto-invio", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),

  listListino: (includeInactive = false) =>
    apiFetch<Regola[]>(`/admin/listino?include_inactive=${includeInactive}`),

  createRegola: (body: RegolaInput) =>
    apiFetch<Regola>("/admin/listino", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateRegola: (id: string, body: RegolaInput) =>
    apiFetch<Regola>(`/admin/listino/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  toggleAttivo: (id: string, attivo: boolean) =>
    apiFetch<Regola>(`/admin/listino/${id}/attivo`, {
      method: "PATCH",
      body: JSON.stringify({ attivo }),
    }),

  deleteRegola: (id: string) =>
    apiFetch<void>(`/admin/listino/${id}`, { method: "DELETE" }),

  simula: (agenzia_id: string, offerta_id?: string) =>
    apiFetch<SimulaResult>("/admin/simula", {
      method: "POST",
      body: JSON.stringify({ agenzia_id, offerta_id: offerta_id ?? null }),
    }),

  lookupFornitori: () =>
    apiFetch<string[]>("/admin/lookup/fornitori"),

  lookupAgenzie: (q?: string) =>
    apiFetch<LookupItem[]>(`/admin/lookup/agenzie${q ? `?q=${encodeURIComponent(q)}` : ""}`),

  lookupOfferte: (q?: string) =>
    apiFetch<LookupItem[]>(`/admin/lookup/offerte${q ? `?q=${encodeURIComponent(q)}` : ""}`),

  commissioni: (from: string, to: string, agenziaIds: string[] = []) =>
    apiFetch<CommissioniCross>(`/admin/commissioni?${rangeParams(from, to, agenziaIds)}`),
  commissioniSerie: (from: string, to: string, agenziaIds: string[] = []) =>
    apiFetch<SerieOut>(`/admin/commissioni/serie?${rangeParams(from, to, agenziaIds)}`),
  commissioniOperatori: (from: string, to: string, agenziaIds: string[] = []) =>
    apiFetch<OperatoriOut>(`/admin/commissioni/operatori?${rangeParams(from, to, agenziaIds)}`),
  commissioniPipeline: (from: string, to: string, agenziaIds: string[] = []) =>
    apiFetch<PipelineOut>(`/admin/commissioni/pipeline?${rangeParams(from, to, agenziaIds)}`),
  margine: (from: string, to: string, agenziaIds: string[] = []) =>
    apiFetch<MargineOut>(`/admin/margine?${rangeParams(from, to, agenziaIds)}`),

  reconcilePreview: (force: boolean) =>
    apiFetch<ReconcilePreview>("/admin/reconcile/preview", {
      method: "POST",
      body: JSON.stringify({ force }),
    }),

  reconcileRun: (force: boolean) =>
    apiFetch<{ job_id: string; stato: string }>("/admin/reconcile/run", {
      method: "POST",
      body: JSON.stringify({ force }),
    }),

  reconcileStatus: (jobId: string) =>
    apiFetch<ReconcileStatus>(`/admin/reconcile/status/${jobId}`),

  inviabiliA2A: (params: A2AInviabiliParams = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
    });
    const q = qs.toString();
    return apiFetch<A2AInviabili>(`/admin/a2a/inviabili${q ? `?${q}` : ""}`);
  },
  kpiA2A: () => apiFetch<A2AKpi>("/admin/a2a/kpi"),
  prefillA2A: (oppId: string) =>
    apiFetch<A2APrefill>(`/admin/a2a/prefill/${oppId}`),
  // a2a_opp_id_atteso: l'id A2A che la pagina vedeva ("" = nessuna bozza); se nel CRM è
  // cambiato il backend risponde 409 pagina_non_aggiornata (pagina rimasta indietro).
  inviaA2A: (opp_id: string, account_id: string, force = false, submit = false, a2a_opp_id_atteso?: string) =>
    apiFetch<A2AInviaResult>("/admin/a2a/invia", {
      method: "POST",
      body: JSON.stringify({ opp_id, account_id, force, submit, a2a_opp_id_atteso }),
    }),
  // submit incrementale: invio definitivo della bozza già preparata (solo segment 7)
  finalizzaA2A: (opp_id: string, a2a_opp_id_atteso?: string) =>
    apiFetch<A2AInviaResult>("/admin/a2a/finalizza", {
      method: "POST",
      body: JSON.stringify({ opp_id, account_id: "", a2a_opp_id_atteso }),
    }),
  inviaA2AStatus: (jobId: string) =>
    apiFetch<A2AStatus>(`/admin/a2a/status/${jobId}`),
  annullaA2A: (opp_id: string) =>
    apiFetch<{ annullata: boolean; a2a_opp_id?: string }>("/admin/a2a/annulla", {
      method: "POST",
      body: JSON.stringify({ opp_id }),
    }),

  inviabiliSentra: (params: A2AInviabiliParams = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
    });
    const q = qs.toString();
    return apiFetch<SentraInviabili>(`/admin/sentra/inviabili${q ? `?${q}` : ""}`);
  },
  kpiSentra: () => apiFetch<SentraKpi>("/admin/sentra/kpi"),
  prefillSentra: (oppId: string) =>
    apiFetch<SentraPrefill>(`/admin/sentra/prefill/${oppId}`),
  inviaSentra: (opp_id: string, invia_sms = false, force = false) =>
    apiFetch<SentraInviaResult>("/admin/sentra/invia", {
      method: "POST",
      body: JSON.stringify({ opp_id, invia_sms, force }),
    }),
  sentraStatus: (jobId: string) =>
    apiFetch<SentraStatus>(`/admin/sentra/status/${jobId}`),
  annullaSentra: (opp_id: string) =>
    apiFetch<{ annullata: boolean; note?: string }>("/admin/sentra/annulla", {
      method: "POST",
      body: JSON.stringify({ opp_id }),
    }),

  // login OTP relay: ri-loggare la sessione Sentra quando scaduta (nessun contatto cliente)
  sentraLoginStart: () =>
    apiFetch<SentraLoginStartOut>("/admin/sentra/login/start", { method: "POST" }),
  sentraLoginOtp: (login_id: string, otp: string) =>
    apiFetch<{ ok: boolean }>("/admin/sentra/login/otp", {
      method: "POST",
      body: JSON.stringify({ login_id, otp }),
    }),
  sentraLoginStatus: (login_id: string) =>
    apiFetch<SentraLoginStatusOut>(`/admin/sentra/login/status/${login_id}`),

  // login OTP relay Iren: stabilire la sessione irenforce (OTP via email, nessun contatto cliente)
  irenLoginStart: () =>
    apiFetch<IrenLoginStartOut>("/admin/iren/login/start", { method: "POST" }),
  irenLoginOtp: (login_id: string, otp: string) =>
    apiFetch<{ ok: boolean }>("/admin/iren/login/otp", {
      method: "POST",
      body: JSON.stringify({ login_id, otp }),
    }),
  irenLoginStatus: (login_id: string) =>
    apiFetch<IrenLoginStatusOut>(`/admin/iren/login/status/${login_id}`),

  // invio pratiche Iren (Fase 2a — v1 solo switch/Cambio, submit=true manda l'email di firma)
  inviabiliIren: (params: A2AInviabiliParams = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
    });
    const q = qs.toString();
    return apiFetch<IrenInviabili>(`/admin/iren/pratiche${q ? `?${q}` : ""}`);
  },
  kpiIren: () => apiFetch<IrenKpi>("/admin/iren/kpi"),
  prefillIren: (oppId: string) =>
    apiFetch<IrenPrefill>(`/admin/iren/prefill/${oppId}`),
  inviaIren: (opp_id: string, force = false, submit = false) =>
    apiFetch<IrenInviaResult>("/admin/iren/invia", {
      method: "POST",
      body: JSON.stringify({ opp_id, force, submit }),
    }),
  statusIren: (jobId: string) =>
    apiFetch<IrenStatus>(`/admin/iren/status/${jobId}`),
  azzeraIren: (opp_id: string) =>
    apiFetch<{ azzerata: boolean }>("/admin/iren/azzera-stato", {
      method: "POST",
      body: JSON.stringify({ opp_id }),
    }),

  // storni: commissioni maturate su pratiche poi annullate a mese chiuso
  storni: (params: { mese?: string; agenzia_id?: string; stato?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const q = qs.toString();
    return apiFetch<Storno[]>(`/admin/storni${q ? `?${q}` : ""}`);
  },
  segnaStorno: (id: string, recuperato: boolean, note?: string) =>
    apiFetch<Storno>(`/admin/storni/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ recuperato, note }),
    }),

  // affiliazione verificata: richieste, inviti, KPI funnel, anomalie
  affListaRichieste: (stato?: string) =>
    apiFetch<AffRichiesta[]>(`/admin/affiliazione/richieste${stato ? `?stato=${stato}` : ""}`),

  affConfermaPec: (id: number, pec: string) =>
    apiFetch<AffEsitoInvio>(`/admin/affiliazione/richieste/${id}/pec`, {
      method: "POST",
      body: JSON.stringify({ pec }),
    }),

  affReminder: (id: number) =>
    apiFetch<AffEsitoInvio>(`/admin/affiliazione/richieste/${id}/reminder`, { method: "POST" }),

  affRifiuta: (id: number, motivo: string) =>
    apiFetch<{ ok: boolean }>(`/admin/affiliazione/richieste/${id}/rifiuta`, {
      method: "POST",
      body: JSON.stringify({ motivo }),
    }),

  affProvisioningRetry: (id: number) =>
    apiFetch<{ ok: boolean }>(`/admin/affiliazione/richieste/${id}/provisioning-retry`, {
      method: "POST",
    }),

  affKpi: () => apiFetch<AffKpi[]>("/admin/affiliazione/kpi"),

  affAnomalie: () => apiFetch<AffAnomalie>("/admin/affiliazione/anomalie"),

  affListaInviti: () => apiFetch<AffInvito[]>("/admin/affiliazione/inviti"),

  affCreaInvito: (nome: string) =>
    apiFetch<AffInvito>("/admin/affiliazione/inviti", {
      method: "POST",
      body: JSON.stringify({ nome }),
    }),

  affToggleInvito: (id: number) =>
    apiFetch<{ id: number; attivo: boolean }>(`/admin/affiliazione/inviti/${id}/toggle`, {
      method: "POST",
    }),

  // catalogo utenze fornitore: quali credenziali lavorano le pratiche, per quali agenzie
  utenzeCatalogo: () => apiFetch<CatalogoUtenze>("/admin/utenze"),

  utenzeCrea: (body: NuovaUtenzaBody) =>
    apiFetch<UtenzaCatalogo>("/admin/utenze", { method: "POST", body: JSON.stringify(body) }),

  // agenzie con pratiche in coda e nessuna utenza assegnata: l'avviso in testa alla pagina
  utenzeNonAssegnate: (fornitore = "A2A") =>
    apiFetch<AgenziaNonAssegnata[]>(`/admin/utenze/non-assegnate?fornitore=${fornitore}`),

  utenzeEventi: (limit = 100) => apiFetch<UtenzaEvento[]>(`/admin/utenze/eventi?limit=${limit}`),

  utenzeAgenzie: (q: string) =>
    apiFetch<LookupItem[]>(`/admin/utenze/agenzie${q ? `?q=${encodeURIComponent(q)}` : ""}`),

  utenzeCollegaAgenzia: (id: number, espo_account_id: string) =>
    apiFetch<{ ok: boolean }>(`/admin/utenze/${id}/agenzie`, {
      method: "POST",
      body: JSON.stringify({ espo_account_id }),
    }),

  utenzeScollegaAgenzia: (id: number, espo_account_id: string) =>
    apiFetch<{ scollegata: boolean; utenza_eliminata: boolean }>(
      `/admin/utenze/${id}/agenzie/${espo_account_id}`, { method: "DELETE" },
    ),

  utenzeRiprova: (id: number) =>
    apiFetch<{ job_id: string }>(`/admin/utenze/${id}/riprova`, { method: "POST" }),

  utenzeProva: (jobId: string) => apiFetch<ProvaAccessoStato>(`/admin/utenze/prova/${jobId}`),

  utenzePredefinita: (fornitore: string, body: UtenzaPredefinitaBody) =>
    apiFetch<{ job_id: string }>(`/admin/utenze/predefinita/${fornitore}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  utenzeElimina: (id: number) =>
    apiFetch<{ agenzie_scollegate: number }>(`/admin/utenze/${id}`, { method: "DELETE" }),

  utenzeImpostazioni: (sessioni_browser_max: number) =>
    apiFetch<{ sessioni_browser_max: number }>("/admin/utenze/impostazioni", {
      method: "PUT",
      body: JSON.stringify({ sessioni_browser_max }),
    }),

  // rete: responsabili dal CRM, schede agenzia, incarichi (assegna/sposta/togli), registro
  reteResponsabili: () => apiFetch<ReteResponsabile[]>("/admin/rete/responsabili"),

  reteCreaResponsabile: (body: ReteResponsabileBody) =>
    apiFetch<ReteResponsabile>("/admin/rete/responsabili", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  reteAggiornaResponsabile: (id: number, body: ReteResponsabileUpdate) =>
    apiFetch<ReteResponsabile>(`/admin/rete/responsabili/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  reteAgenzie: () => apiFetch<ReteScheda[]>("/admin/rete/agenzie"),

  reteCompletaScheda: (body: ReteCompletaBody) =>
    apiFetch<ReteScheda>("/admin/rete/agenzie/completa", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // assegna o sposta: se l'agenzia ha già un incarico aperto il backend lo chiude a `dal`
  reteApriIncarico: (body: ReteIncaricoBody) =>
    apiFetch<ReteScheda>("/admin/rete/incarichi", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  reteChiudiIncarico: (incaricoId: number, al: string) =>
    apiFetch<{ incarico_id: number; al: string }>(`/admin/rete/incarichi/${incaricoId}/chiudi`, {
      method: "POST",
      body: JSON.stringify({ al }),
    }),

  reteEventi: (limit = 100) => apiFetch<ReteEvento[]>(`/admin/rete/eventi?limit=${limit}`),

  // quote del mese: la tabella per responsabile e, a richiesta, le righe di uno solo
  reteQuote: (year: number, month: number) =>
    apiFetch<ReteQuotaResponsabile[]>(`/admin/rete/quote?year=${year}&month=${month}`),

  reteQuoteResponsabile: (responsabileId: number, year: number, month: number) =>
    apiFetch<QuotaReteResponse>(
      `/admin/rete/quote/${responsabileId}?year=${year}&month=${month}`,
    ),
};
